import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from "react"
import tw, { styled } from "twin.macro"
import { ProfileProps } from "../profile"
import {
  CreateQuestionMutationFn,
  ProfileOnGetProfileFragment,
  UserOnGetUserFragmentFragment,
} from "../../graphql/generated/graphql"
import { Events, registerEvent } from "../../analytics"
import { SupportPayment } from "./supportPayment"
import { ThankYou } from "./thankYou"
import { QuestionForm } from "./questionForm"
import { Preparing } from "./preparing"
import { RedirectContext } from "../../utils/context"
import { isMobile } from "react-device-detect"
import { Modal } from "../modal"

export const SidebarContainer = styled.div(() => [
  tw`bg-background flex flex-col px-6 py-8 w-full tablet-lg:(w-80) border-2 border-base-1 rounded-lg shadow-base-2 relative overflow-y-scroll desktop:(sticky top-20)`,
])

export interface AskQuestionProps {
  profile: ProfileOnGetProfileFragment
  questionBody: string
  setQuestionBody: Dispatch<SetStateAction<string>>
  createQuestion?: CreateQuestionMutationFn
  step?: AskQuestionStep
  setStep?: Dispatch<SetStateAction<AskQuestionStep>>
  refetchProfileData?: () => Promise<Record<string, any>>
  setQuestionId?: Dispatch<SetStateAction<string>>
}
export interface SupportPaymentProps {
  User: UserOnGetUserFragmentFragment
  setShowPaymentGate?: Dispatch<SetStateAction<boolean>>
  storybookError?: string
}

export enum AskQuestionStep {
  QuestionForm = "QUESTION_FORM",
  Payment = "PAYMENT",
  Preparing = "PREPARING",
  ThankYou = "THANK_YOU",
}

export const AskQuestion = (props: ProfileProps) => {
  const { createQuestion, refetchProfileData, profile, profileData } = props
  const { User } = profileData
  const { getData } = useContext(RedirectContext)
  const isAskQuestionData = !!getData().data.questionBody
  const redirectData: string = isAskQuestionData ? getData().data.questionBody : ""
  const [questionId, setQuestionId] = useState<string>("")
  const [step, setStep] = useState<AskQuestionStep>(AskQuestionStep.QuestionForm)
  const [questionBody, setQuestionBody] = useState<string>(redirectData)
  const [startedQuestionBodyInput, setStartedQuestionBodyInput] = useState<boolean>(false)
  const askQuestionProps: AskQuestionProps = {
    questionBody,
    setQuestionBody,
    createQuestion,
    profile,
    setStep,
    refetchProfileData,
    setQuestionId,
  }

  const renderSteps = () => {
    switch (step) {
      case AskQuestionStep.Preparing:
        return <Preparing {...{ setStep }} />
      case AskQuestionStep.Payment:
        return !!User ? <SupportPayment {...{ ...askQuestionProps, User }} /> : <></>
      case AskQuestionStep.ThankYou:
        return <ThankYou {...{ questionId, profile, setQuestionBody, setShow }} />
      default:
        return <QuestionForm {...askQuestionProps} />
    }
  }
  const [show, setShow] = useState(false)
  const modalProps = {
    show,
    setShow,
    component: <>{renderSteps()}</>,
    onAfterClose: () => setStep(AskQuestionStep.QuestionForm),
  }

  useEffect(() => {
    if (questionBody.length > 0 && !startedQuestionBodyInput) {
      registerEvent(Events.StartedQuestionBodyInput)
      setStartedQuestionBodyInput(true)
    }
  }, [questionBody])
  useEffect(() => {
    step !== AskQuestionStep.QuestionForm && setShow(true)
  }, [isMobile, step])

  return (
    <>
      {isMobile ? (
        <SidebarContainer>
          <QuestionForm {...askQuestionProps} />
          <Modal {...modalProps} />
        </SidebarContainer>
      ) : (
        <SidebarContainer data-testid="ask-question-module">{renderSteps()}</SidebarContainer>
      )}
    </>
  )
}
